import { useEffect, useState } from 'react'
import { Alert, App, Form, Modal, Select, Space, Tag, Typography } from 'antd'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { useAuth } from '@/shared/auth/useAuth'
import { SCOPE_TYPES, type ScopeType } from '@/shared/auth/permissions'
import { roleName } from '@/shared/lib/roleName'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { assignRole, fetchVenueTargets, type PanelUser, type Role } from './identityApi'
import { OrgUnitTreeMultiSelect } from './OrgUnitTreeSelect'
import { useOrgUnits } from './useOrgUnits'

/**
 * Doira kengligi — backend `ScopeType.breadth` ning AYNAN nusxasi.
 *
 * ⚠️ Bu QULAYLIK, xavfsizlik EMAS: haqiqiy to'siq `RoleServiceImpl.assignRole`
 * ichida (`isAtLeastAsBroadAs`). Bu yerda takrorlashning yagona maqsadi —
 * operatorga 403 bilan tugaydigan variantni ko'rsatmaslik.
 *
 * ⚠️ Bu CHIZIQLI TARTIB EMAS: `SUBTREE` va `VENUE` turli o'lchovlar. Raqamlar
 * faqat "kengroqmi" degan qo'pol savolga javob beradi — backendda ham shunday.
 */
const SCOPE_BREADTH: Record<ScopeType, number> = {
  GLOBAL: 4,
  TENANT: 3,
  SUBTREE: 2,
  VENUE: 1,
  OWN: 0,
}

interface AssignRoleModalProps {
  open: boolean
  user: PanelUser | null
  roles: readonly Role[]
  onClose: () => void
}

interface FormValues {
  roleId: string
  scopeType: ScopeType
  scopeId?: string
  /** SUBTREE: bitta rol bir nechta filialga — har biri alohida biriktirish (ADR-046) */
  scopeIds?: string[]
}

/**
 * Foydalanuvchiga rol berish.
 *
 * Uch narsa bir joyda ko'rsatiladi, chunki operator "bu odam nima qila
 * oladi" degan savolga bir qarashda javob topishi kerak:
 *   · rol nomi va kodi
 *   · rol ichidagi huquqlar soni va XAVFLILARI
 *   · doira — "kimga nisbatan" (huquq "nima qila oladi" ni aytadi, doira
 *     "kimga nisbatan"; ikkinchisini unutish resellerlar bir-birining
 *     mijozini ko'radigan holatga olib keladi)
 */
export function AssignRoleModal({ open, user, roles, onClose }: AssignRoleModalProps) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()
  const { user: actor } = useAuth()

  const [roleId, setRoleId] = useState<string | null>(null)
  const [scopeType, setScopeType] = useState<ScopeType>('TENANT')

  const selectedRole = roles.find((r) => r.id === roleId) ?? null

  // Chaqiruvchining eng KENG doirasi — undan kengrog'ini bera olmaydi.
  //
  // ⚠️ Boshlang'ich qiymat `SCOPE_BREADTH.OWN` (0), `-1` EMAS: doirasiz
  // foydalanuvchida ro'yxat BUTUNLAY bo'sh qolardi va forma "hech narsa
  // tanlab bo'lmaydi" holatiga tushardi — sababi ko'rinmagan holda.
  // Eng tor variant qolgani afzal: server baribir o'z tekshiruvini
  // qiladi va noto'g'ri urinishni rad etadi.
  const ownBreadth = (actor?.scopes ?? []).reduce(
    (max, scope) => Math.max(max, SCOPE_BREADTH[scope.type]),
    SCOPE_BREADTH.OWN,
  )
  const allowedScopes = SCOPE_TYPES.filter((scope) => SCOPE_BREADTH[scope] <= ownBreadth)

  const venues = useQuery({
    queryKey: queryKeys.identity.scopeTargets('VENUE'),
    queryFn: fetchVenueTargets,
    enabled: open && scopeType === 'VENUE',
    retry: false,
  })
  const orgUnits = useOrgUnits(open && scopeType === 'SUBTREE')

  useEffect(() => {
    if (!open) return
    setRoleId(null)
    setScopeType('TENANT')
    form.resetFields()
  }, [open, form])

  const grant = useMutation({
    mutationFn: async (values: FormValues) => {
      const targets = values.scopeType === 'SUBTREE' ? (values.scopeIds ?? []) : [values.scopeId ?? null]
      for (const scopeId of targets) {
        await assignRole(user?.id ?? '', { roleId: values.roleId, scopeType: values.scopeType, scopeId })
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.identity.users })
      void queryClient.invalidateQueries({ queryKey: queryKeys.identity.roles })
      // X-SIB-004 — muvaffaqiyatli amaldan keyin ANIQ tasdiq: oyna
      // yopiladi va ekranda hech qanday iz qolmasdi
      void message.success(t('common.toastSaved'))
      form.resetFields()
      onClose()
    },
  })

  /** Bekor qilib chiqish — xato xabari ham qolmasin, keyingi ochilishga o'tmasin */
  const close = () => {
    form.resetFields()
    grant.reset()
    onClose()
  }

  /** Rol tanlanganda doira uning standart qiymatiga o'tadi — operator har safar o'ylamasin */
  const onRoleChange = (value: string) => {
    setRoleId(value)
    const role = roles.find((r) => r.id === value)
    if (!role) return
    const next = SCOPE_BREADTH[role.defaultScope] <= ownBreadth ? role.defaultScope : 'OWN'
    setScopeType(next)
    form.setFieldsValue({ scopeType: next, scopeId: undefined, scopeIds: [] })
  }

  const venueOptions = (venues.data ?? []).map((venue) => ({ value: venue.id, label: venue.label }))
  const targetUnavailable =
    scopeType === 'VENUE'
      ? venues.isError || (venues.isSuccess && venueOptions.length === 0)
      : !orgUnits.loading && (!orgUnits.reliable || orgUnits.units.length === 0)

  const heldScopes = new Set((user?.roles ?? []).map((assignment) => assignment.scopeType))
  const mixesScopes =
    (scopeType === 'SUBTREE' && heldScopes.has('TENANT')) || (scopeType === 'TENANT' && heldScopes.has('SUBTREE'))

  return (
    <Modal
      open={open}
      title={t('identity.roles.assignTitle')}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => grant.mutate(values))}
      confirmLoading={grant.isPending}
      okText={t('identity.roles.assign')}
      cancelText={t('common.cancel')}
      destroyOnHidden
    >
      <Typography.Paragraph>
        <Typography.Text strong>{user?.username}</Typography.Text>
      </Typography.Paragraph>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('identity.roles.escalationNote')}
      />

      {/* 403/409 shu yerda qoladi — oyna ochiq, ya'ni operator xabarni
          amal tugmasi bilan bir ekranda ko'radi. Sahifa banneriga
          yuborilganda u modal niqobi ortida ko'rinmasdi. */}
      {grant.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(grant.error, t)}
        />
      ) : null}

      <Form
        form={form}
        layout="vertical"
        preserve={false}
        initialValues={{ scopeType: 'TENANT' as ScopeType }}
      >
        <Form.Item
          name="roleId"
          label={t('identity.roles.role')}
          rules={[{ required: true, message: t('identity.roles.roleRequired') }]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            onChange={onRoleChange}
            placeholder={t('identity.roles.rolePlaceholder')}
            options={roles.map((role) => ({
              value: role.id,
              label: `${roleName(role, t)} · ${role.code}`,
            }))}
          />
        </Form.Item>

        {selectedRole ? (
          <div style={{ marginBottom: 16 }}>
            <Space size={[4, 4]} wrap>
              <Tag color={selectedRole.system ? 'blue' : 'default'}>
                {selectedRole.system ? t('identity.roles.system') : t('identity.roles.custom')}
              </Tag>
              <Tag>
                {t('identity.roles.permissionCount', {
                  count: selectedRole.permissions.length + selectedRole.unknownPermissions.length,
                })}
              </Tag>
              <Tag>{t('identity.roles.userCount', { count: selectedRole.userCount })}</Tag>
            </Space>
          </div>
        ) : null}

        <Form.Item
          name="scopeType"
          label={t('identity.roles.scope')}
          extra={t('identity.roles.scopeHint')}
          rules={[{ required: true, message: t('identity.roles.scopeRequired') }]}
        >
          <Select
            value={scopeType}
            onChange={(value: ScopeType) => {
              setScopeType(value)
              form.setFieldsValue({ scopeId: undefined, scopeIds: [] })
            }}
            options={allowedScopes.map((scope) => ({
              value: scope,
              label: `${t(`identity.scope.${scope}`)} — ${t(`identity.scopeHint.${scope}`)}`,
            }))}
          />
        </Form.Item>

        {mixesScopes ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('identity.roles.scopeMixTitle')}
            description={t('identity.roles.scopeMixText')}
          />
        ) : null}

        {scopeType === 'SUBTREE' ? (
          <Form.Item
            name="scopeIds"
            label={t('identity.roles.target.SUBTREE')}
            extra={targetUnavailable ? t('identity.roles.targetUnavailable.SUBTREE') : t('identity.roles.targetMany')}
            rules={[{ required: true, type: 'array', min: 1, message: t('identity.roles.targetRequired') }]}
          >
            <OrgUnitTreeMultiSelect
              units={orgUnits.units}
              types={orgUnits.types}
              loading={orgUnits.loading}
              disabled={targetUnavailable}
            />
          </Form.Item>
        ) : null}
        {scopeType === 'VENUE' ? (
          <Form.Item
            name="scopeId"
            label={t(`identity.roles.target.${scopeType}`)}
            extra={targetUnavailable ? t(`identity.roles.targetUnavailable.${scopeType}`) : undefined}
            rules={[{ required: true, message: t('identity.roles.targetRequired') }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              loading={venues.isPending}
              disabled={targetUnavailable}
              options={venueOptions}
              placeholder={t('identity.roles.targetPlaceholder')}
            />
          </Form.Item>
        ) : null}
      </Form>
    </Modal>
  )
}
